import React, { useMemo, useState } from 'react';
import { Copy } from 'lucide-react';
import type { GameSession } from '@business-game/shared';
import { buildSessionShareLinks, buildSessionShareMessage, type SessionShareMessageLocale } from '../../utils/sessionShare';

type Props = {
  session: GameSession;
};

const LOCALE_OPTIONS: Array<{ value: SessionShareMessageLocale; label: string; flag: string }> = [
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
];

export const SessionSharePanel: React.FC<Props> = ({ session }) => {
  const [locale, setLocale] = useState<SessionShareMessageLocale>('fr');
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(false);

  const links = useMemo(() => buildSessionShareLinks(session.code), [session.code]);
  const message = useMemo(() => buildSessionShareMessage(session, { locale }), [session, locale]);
  const code = session.code.toUpperCase();

  const copyText = async (text: string, kind: 'link' | 'message') => {
    try {
      await navigator.clipboard.writeText(text);
      if (kind === 'link') {
        setCopiedLink(true);
        window.setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopiedMessage(true);
        window.setTimeout(() => setCopiedMessage(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">
          Lien participant
        </label>
        <div className="flex gap-2.5">
          <input
            readOnly
            value={links.direct}
            className="h-[50px] min-w-0 flex-1 rounded-[13px] border-[1.5px] border-slate-200 bg-slate-50 px-[15px] font-mono text-sm font-semibold text-slate-600 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void copyText(links.direct, 'link')}
            className="inline-flex shrink-0 items-center gap-2 rounded-[13px] bg-indigo-600 px-5 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
          >
            <Copy className="h-4 w-4" />
            {copiedLink ? 'Copié' : 'Copier'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 rounded-2xl border-[1.5px] border-slate-200 px-[18px] py-[18px]">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-50">
          <span className="font-mono text-lg font-black tracking-wider text-indigo-600">{code.slice(0, 2)}</span>
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">Code d&apos;accès</p>
          <p className="font-mono text-2xl font-black tracking-[0.12em] text-slate-900">{code}</p>
        </div>
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">Message complet</p>
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
            {LOCALE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setLocale(option.value)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors',
                  locale === option.value
                    ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-100'
                    : 'text-slate-600 hover:text-slate-900',
                ].join(' ')}
              >
                <span aria-hidden="true">{option.flag}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <textarea
          readOnly
          value={message}
          rows={10}
          className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-3 font-mono text-xs leading-relaxed text-slate-700 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void copyText(message, 'message')}
          className={[
            'mt-3 inline-flex items-center gap-2 rounded-[13px] px-4 py-2.5 text-sm font-bold text-white transition-colors',
            copiedMessage ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-violet-600 hover:bg-violet-700',
          ].join(' ')}
        >
          <Copy className="h-4 w-4" />
          {copiedMessage ? 'Message copié' : 'Copier le message'}
        </button>
      </div>
    </div>
  );
};
